import React, {
  Fragment,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import CertificateSample from "../ServicePage/components/CertificateSample";
import TitleSection from "../ServicePage/components/TitleSection";
import {
  ItemScroller,
  PageDataErrorBox,
  ShareButton,
  YoutubeGallery,
} from "../../Components";
import { useApi } from "../../Helpers/ApiHook";
import { useNavigate, useParams } from "react-router-dom";
import _ from "lodash";
import { FullHours, GymProfileSkeletonLoader } from "./components";
import NeedHelp from "../ServicePage/components/NeedHelp";

const GymProfile = () => {
  const { get } = useApi();

  const params = useParams();

  const gymId = params?.gymId || null;
  const [isLoading, setIsLoading] = useState(true);
  const [gymDetails, setGymDetails] = useState();

  const hasFetchedRef = useRef(false);

  const _getGymDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await get(`gyms/show/${gymId}`);
      if (data.status) {
        setGymDetails(data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching trainers: ", error);
    } finally {
      setIsLoading(false);
    }
  }, [get, gymId]);

  useEffect(() => {
    if (!gymId) return;

    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      _getGymDetails();
    }
  }, [gymId, _getGymDetails]);

  if (isLoading) {
    return <GymProfileSkeletonLoader />;
  }

  if (!isLoading && !gymDetails?.facility_name) {
    return <PageDataErrorBox />;
  }

  const _getWhatsAppMsg = (msg = "") => {
    const message = `https://wa.me/91${
      gymDetails?.whatsapp_no || gymDetails?.phone_no || "0000000000"
    }?text=${encodeURIComponent(msg)}`;
    return message;
  };

  const whatsapp_msg =
    "Hello Trainer, I'm interested in your training programs. Can you please share more details?";

  const location = gymDetails?.location || {};

  const locality =
    `${!!location?.city ? location?.city + ", " : ""}${
      !!location?.state ? location?.state + ", " : ""
    }${!!location?.country ? location?.country : ""}` || "India";

  const full_hours = { ...gymDetails?.full_hours };

  const key_points = (gymDetails?.key_points || []).filter((gd) => gd.status);

  const pricing_plans = gymDetails?.pricing_plans || [];
  const amenities = gymDetails?.amenities || [];

  const SocialLinks = () => {
    const social_links_api = gymDetails?.social_links || {};

    const social_media_links = [
      {
        name: "facebook",
        link: social_links_api.facebook || "",
        image: "/assets/individual/social-media/facebook.png",
      },
      {
        name: "youtube",
        link: social_links_api.youtube || "",
        image: "/assets/individual/social-media/youtube.png",
      },
      {
        name: "linkedin",
        link: social_links_api.linkedin || "",
        image: "/assets/individual/social-media/linkedin.png",
      },
      {
        name: "instagram",
        link: social_links_api.instagram || "",
        image: "/assets/individual/social-media/instagram.png",
      },
    ];
    return (
      <div className="flex justify-center md:justify-start gap-3 text-lg">
        {social_media_links.map((link, index) => {
          return (
            <a
              target="_blank"
              rel="noreferrer"
              href={link.link}
              className=""
              key={index}
            >
              <img className="h-8 w-8" src={link.image} alt={link.name} />
            </a>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full bg-gradient-radial flex flex-col">
      <section className="w-full bg-gradient-radial lg:pt-20 pt-20 pb-2 md:pb-12 lg:py-14">
        <div className="w-full lg:w-full flex flex-col items-center px-6 md:px-14 py-12">
          <div className="relative w-full h-44 flex flex-col items-center md:h-96 rounded-lg shadow-lg">
            <img
              src={
                gymDetails?.banner_img ||
                "/assets/gym-facilities/profile-bg.jpg"
              } // Replace with actual image URL or asset
              alt="gym-banner-image"
              className="object-cover w-full h-full rounded-lg bg-gray-700 opacity-70"
            />
            {/* Logo Box */}
            <div className="absolute flex justify-center items-end bottom-[-3rem] md:bottom-[-4rem]">
              <div className="w-44 md:w-56 lg:w-80 bg-gradient-radial bg-black rounded-xl rounded-tr-[0.4rem] shadow-md border-[3px]">
                <img
                  src={
                    gymDetails?.logo_img ||
                    "/assets/gym-facilities/gym-logo.png"
                  }
                  className="h-24 md:h-32 lg:h-44 w-full object-contain"
                  alt="gym-logo"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Header Section */}
      <section className="w-full bg-gradient-radial py-5 px-8 md:px-20">
        <div className="flex flex-col md:flex-row items-start md:items-center py-5 font-urbanist">
          <div className="relative w-full flex flex-col items-start">
            <h2 className="text-white text-2xl font-semibold mb-1">
              {gymDetails?.facility_name}
            </h2>
            <div className="flex flex-row items-start">
              <img
                src="/assets/gym-facilities/location.png"
                className="h-6 w-6"
                alt="location"
              />
              <p className="text-gray-400 text-sm max-w-lg ml-2">
                {gymDetails?.full_address} {locality}
              </p>
            </div>
          </div>
          <div className="flex items-center w-full mt-3 gap-4 md:mt-0 md:w-auto">
            <a
              target="_blank"
              rel="noreferrer"
              href={gymDetails?.navigation_url}
              className="text-blue-500 hover:underline md:p-0 pl-4"
            >
              NAVIGATE
            </a>

            <div className="ml-auto">
              <ShareButton />
            </div>
          </div>
        </div>

        {/* Tags */}
        {!!key_points.length && (
          <div className="flex flex-wrap gap-6 border-t border-dashed border-t-gray-700 py-8">
            {(key_points || []).map((tag, index) => {
              const radiusClass =
                index % 2 === 1 ? "rounded-tl-md" : "rounded-tr-md";
              return (
                <div
                  key={index}
                  className={`flex flex-row items-center bg-[#171A26] px-5 py-2 md:py-2 rounded-3xl ${radiusClass} gap-3 transition-transform hover:scale-105`}
                >
                  <img src={tag.img} className="w-4 h-4" alt={tag.name} />
                  <div className="text-white text-[0.75rem] font-normal font-urbanist leading-normal">
                    {tag.name}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex flex-col gap-3 border-t border-b border-dashed border-gray-700 py-5">
          <div className="flex flex-row items-start justify-center md:justify-start gap-2 font-urbanist">
            <SocialLinks />
          </div>
        </div>

        <FullHours full_hours={full_hours} />

        <div className="flex flex-col gap-3 border-t border-b border-dashed border-gray-700 py-5">
          <div className="flex flex-row items-start gap-2 font-urbanist">
            <div className="h-6 w-6" />
            <div className="flex flex-col items-start gap-2 font-urbanist">
              <div className="text-md text-white max-w-lg m-0">
                <p>Have any Queries?</p>
                <p className="text-white text-sm">
                  Contact Centre support to assist you more
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-2 md:gap-6">
            {[
              {
                name: "Call",
                image: "/assets/individual/Call.png",
                link: `tel:${gymDetails?.phone_no}`,
              },
              {
                name: "WhatsApp",
                image: "/assets/individual/WhatsApp.png",
                link: `${_getWhatsAppMsg(whatsapp_msg)}`,
              },
              {
                name: "Mail",
                image: "/assets/individual/Mail.png",
                link: `mailto:${
                  gymDetails?.email || ""
                }?subject=${encodeURIComponent(
                  "Training Inquiry"
                )}&body=${encodeURIComponent(
                  "Hello Trainer, I'm interested in your training programs. Can you please share more details?"
                )}`,
              },
            ].map((buttonDetails, index) => {
              return (
                <button
                  onClick={() => {
                    window.open(buttonDetails?.link, "_blank");
                  }}
                  key={index}
                  className="group flex items-center bg-white border border-gray-300 text-gray-900 font-medium p-[0.15rem] rounded-xl rounded-tr-lg shadow-sm hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                  <span className="px-2 font-urbanist text-[0.75rem] md:text-sm group-hover:scale-105">
                    {buttonDetails.name}
                  </span>
                  <span className="flex items-center justify-center w-5 h-5 rounded-xl rounded-tr-md p-1 transition bg-black group-hover:scale-105">
                    <img
                      className="transition-transform"
                      src={buttonDetails.image}
                      alt={buttonDetails.name}
                    />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>
      {/* What We Offer Section */}
      {(gymDetails?.services || []).length > 0 && (
        <Fragment>
          <section className="w-full bg-gradient-radial py-2 lg:py-4">
            <div className="container mx-auto px-6 lg:px-24">
              <div className="flex flex-col items-center">
                <TitleSection
                  title={"What we Offer"}
                  description="From beginners to athletes, we guide you every step—building confidence, strength, and a healthier lifestyle."
                />

                <ItemScroller
                  schema={{
                    image: "img",
                    name: "title",
                  }}
                  items={gymDetails?.services || []}
                />
              </div>
            </div>
          </section>
          <section className="w-full bg-gradient-radial py-5 px-20">
            <div className="flex flex-col border-t border-dashed border-t-gray-700"></div>
          </section>
        </Fragment>
      )}

      {/* Affiliation Certificate */}
      <CertificateSample
        title="Affiliation Certificate"
        imagePath={gymDetails?.affiliation_certificate}
      />

      <section className="w-full bg-gradient-radial py-5 px-6 md:px-20">
        <div className="flex flex-col border-t border-dashed border-t-gray-700"></div>
      </section>

      {/* Our Plans */}
      {!!pricing_plans.length && (
        <section className="w-full bg-gradient-radial py-5 lg:py-4">
          <div className="container mx-auto px-6 lg:px-24">
            <div className="flex flex-col items-center">
              <div className="w-full md:w-[60%] flex flex-col items-center gap-6 mb-10">
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold font-urbanist text-white text-center sm:text-center">
                  Our Plans
                </h2>
                <p className="text-sm text-center sm:text-base italic lg:text-xl font-urbanist text-stone-300 px-8">
                  From beginners to athletes, we guide you every step—building
                  confidence, strength, and a healthier lifestyle.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
                {pricing_plans.map((plan, i) => {
                  const plan_wp_msg = `Hello! I saw your *${plan.duration}* plan for ₹${plan.price}. I'd like to know more about the benefits and the joining process. Please assist me. 😊`;

                  return (
                    <div
                      key={i}
                      className="bg-black rounded-2xl rounded-tl-lg p-6 flex flex-col justify-between border-[2px] border-white shadow-md"
                    >
                      <div className="w-full flex flex-col items-center text-white font-urbanist gap-2">
                        <div className="w-full flex flex-col items-center pb-2 mb-2 border-b border-dashed border-b-gray-700 text-center">
                          <div className="flex flex-row items-center text-center">
                            <strong className="text-3xl font-semibold mb-1">
                              {plan.price}
                            </strong>
                            <span className="text-base">/ {plan.duration}</span>
                          </div>
                          <div className="flex flex-row items-start gap-1 my-2">
                            <img
                              className="w-4 h-4 lg:w-4 lg:h-4 flex-shrink-0 rounded-full"
                              src="/assets/gym-facilities/discount.png"
                              alt="discount"
                            />
                            <p className="text-xs">{plan.note}</p>
                          </div>
                        </div>
                        <div className="w-full flex flex-col items-start">
                          <h4 className="font-semibold mb-2">
                            What's Included?
                          </h4>
                          <ul className="text-gray-400 text-sm mb-6 space-y-3 list-disc list-inside">
                            {plan.features.map((feature, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <img
                                  className="w-6 h-6 lg:w-5 lg:h-5 flex-shrink-0 rounded-full"
                                  src={`/assets/gym-facilities/${
                                    feature.status ? "check" : "times"
                                  }-circle.png`}
                                  alt="check-circle"
                                />
                                <p className="text-neutral-200 sm:text-sm text-base">
                                  {feature.title || "-"}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <a
                        href={_getWhatsAppMsg(plan_wp_msg)}
                        target="_blank"
                        rel="noreferrer"
                        className="bg-white text-gray-900 text-center font-semibold py-2 rounded-xl hover:bg-yellow-400 transition-colors"
                      >
                        Join Now
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Amenities */}
      {amenities.length > 0 && (
        <section className="w-full bg-gradient-radial py-5 lg:py-10">
          <div className="container mx-auto px-4 lg:px-24">
            <div className="flex flex-col items-center">
              <div className="w-full md:w-[60%] flex flex-col items-center gap-6 mb-4">
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold font-urbanist text-white text-center sm:text-center">
                  Amenities
                </h2>
              </div>
              <div className="flex flex-row flex-wrap gap-4 justify-center w-[70%]">
                {amenities.map((amenity, i) => {
                  const radiusClass =
                    i % 2 === 1 ? "rounded-tl-md" : "rounded-tr-md";
                  return (
                    <div
                      key={i}
                      className={`w-32 bg-gradient-radial bg-black rounded-xl ${radiusClass} px-6 py-3 flex flex-col justify-between text-center text-xs`}
                    >
                      <div className="p-3">
                        <img
                          className="w-full h-full object-contain object-center"
                          src={
                            amenity?.img ||
                            `/assets/gym-facilities/amenities/Bike.png`
                          }
                          alt={amenity.name}
                        />
                      </div>
                      <div className="text-white text-[0.85rem] mt-1">
                        {amenity.title}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Client Onboarding Process */}
      {/* Our Plans */}

      <section className="w-full bg-gradient-radial py-5 px-6 lg:py-10">
        <div className="flex flex-col">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex flex-col">
              <div className="flex flex-col items-center gap-6 mb-4">
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold font-urbanist text-white text-center sm:text-center">
                  Client Onboarding Process
                </h2>
                <p className="text-sm text-center sm:text-base italic lg:text-xl font-urbanist text-stone-300">
                  Easy-to-follow workout guides, fitness tips, and coaching
                  videos designed to help you get stronger, healthier, and more
                  confident every day.
                </p>
              </div>
              <ul className="border-2 border-white rounded-2xl rounded-tr-[3rem] rounded-br-[3rem] p-5 text-left max-w-md mx-auto space-y-3 text-gray-400 text-sm list-disc list-inside mt-5">
                {[
                  "Meet & Greet + Gym Tour",
                  "Health & Safety Orientation",
                  "Comprehensive Fitness Assessment",
                  "Personalized Workout Plan Design",
                  "Progress Monitoring & Support",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <img
                      className="w-6 h-6 lg:w-5 lg:h-5 flex-shrink-0 rounded-full"
                      src="/assets/gym-facilities/check-circle.png"
                      alt="check-circle"
                    />
                    <p className="text-neutral-200 sm:text-sm text-base">
                      {item}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Content Section */}
      {(gymDetails?.youtube_links || []).length > 0 && (
        <section className="w-full bg-gradient-radial py-5 lg:py-5">
          <div className="container mx-auto px-4 lg:px-24">
            <YoutubeGallery
              title={"Visual Content on YouTube"}
              tagline={
                "Easy-to-follow workout guides, fitness tips, and coaching videos designed to help you get stronger, healthier, and more confident every day."
              }
              items={gymDetails?.youtube_links || []}
            />
          </div>
        </section>
      )}

      <NeedHelp
        title={`Join ${gymDetails?.facility_name || ""} Gym Today!`}
        tagline={`Transform Every Day with ${
          gymDetails?.facility_name || ""
        } Gym`}
        email={gymDetails?.email}
        phone_no={gymDetails?.phone_no}
      />
    </div>
  );
};

export { GymProfile };
